import type { PlayerId } from '@webgame/shared';
import { useEffect } from 'preact/hooks';
import type { DevTools, GameClient } from '../../core';

/*
 * «Watch in god mode» from F4 (B131): a scenario entered to be looked at, not played. The reset
 * ends every session, so the wish waits in this tab's storage (never another's: the bot's tab keeps
 * its own) across the sign-in screen, which signs in as the scenario's first human by itself, and
 * the world, where the character turns god and goes where the scenario's action is.
 */

const KEY = 'webgame.watch';
/** God mode's pace while watching: quick to move around what happens. */
export const WATCH_SPEED = 4;

export interface WatchWish {
  readonly scenario: string;
  /** Where its action happens (world metres), or null: the character's own start. */
  readonly at: { readonly x: number; readonly y: number } | null;
  /** Whether what comes out for the character still comes (`devSetGod.perceived`). */
  readonly perceived: boolean;
}

const isWish = (value: unknown): value is WatchWish => {
  if (typeof value !== 'object' || value === null) return false;
  const wish = value as Partial<WatchWish>;
  return (
    typeof wish.scenario === 'string' &&
    (wish.at === null || typeof wish.at === 'object') &&
    typeof wish.perceived === 'boolean'
  );
};

/** Leaves the wish for after the reset. */
export function wishToWatch(wish: WatchWish): void {
  try {
    globalThis.sessionStorage.setItem(KEY, JSON.stringify(wish));
  } catch {
    // Without storage the reset still happens: only the watching is lost.
  }
}

/** The wish left before a reset, if any. */
export function pendingWatch(): WatchWish | null {
  try {
    const raw = globalThis.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isWish(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function forgetWatch(): void {
  try {
    globalThis.sessionStorage.removeItem(KEY);
  } catch {
    // Nothing kept, nothing to forget.
  }
}

/**
 * Once the character stands in the world, a wish left before a reset is carried out: the other
 * seeded characters stand in (their bot tabs need not be open), god mode (seeing everything, as a
 * reviewer does; perceived where the scenario asks it) and the scenario's watch point. Done once,
 * then forgotten.
 */
export function useWatchArrival(
  client: GameClient,
  devTools: DevTools,
  playerId: PlayerId,
  available: boolean,
): void {
  useEffect(() => {
    if (!available || !pendingWatch()) return;
    let done = false;
    const arrive = (active: unknown): void => {
      const wish = pendingWatch();
      if (done || !active || !wish) return;
      done = true;
      forgetWatch();
      void devTools
        .standIns(true)
        .then(() =>
          devTools.god(playerId, true, WATCH_SPEED, { seeAll: true, perceived: wish.perceived }),
        )
        .then(() => (wish.at ? devTools.teleport(playerId, wish.at) : false))
        .catch(() => undefined);
    };
    // Already in the world when it mounts, or once it gets there.
    arrive(client.world.active.get());
    return client.world.active.subscribe(arrive);
  }, [client, devTools, playerId, available]);
}

/**
 * At the sign-in screen after a watch's reset: the character to sign in as, the seeded list's first
 * human (a scenario's watcher, Aria), or null when no watch is pending or none is human.
 */
export function watcherAmong<T extends { readonly isBot: boolean }>(
  seeded: readonly T[],
): T | null {
  if (!pendingWatch()) return null;
  return seeded.find((player) => !player.isBot) ?? null;
}
